/**
 * IPFS Service Worker Initialization
 *
 * Waits for the unified PWA service worker to be ready, then sends config
 */

import { Logger } from 'besonders-logger'
import { setConfig, isIpfsSWReady } from './ipfs-sw-client'
import { storageState } from './storage'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG, { prefix: '[ipfs-sw-init]' })

let initPromise: Promise<void> | null = null

/**
 * Initialize the IPFS service worker
 * Safe to call multiple times - will only initialize once
 */
export async function initIpfsSW(): Promise<void> {
	if (initPromise) return initPromise

	initPromise = (async () => {
		try {
			LOG('Initializing IPFS SW...')

			// Wait for the unified PWA SW to be ready
			let ready = false
			let attempts = 0
			while (!ready && attempts < 20) {
				ready = await isIpfsSWReady()
				if (!ready) {
					await new Promise(resolve => setTimeout(resolve, 250))
					attempts++
				}
			}

			if (!ready) {
				throw new Error('IPFS SW failed to become ready')
			}

			// Unregister old standalone ipfs-sw.js AFTER unified SW is confirmed active
			await unregisterStaleSW()

			// Send initial config from storage state
			await updateSWConfigFromState()

			LOG('IPFS SW initialized successfully')
		} catch (err) {
			ERROR('Failed to initialize IPFS SW:', err)
			// Don't throw - app can still work without SW
		}
	})()

	return initPromise
}

/**
 * Unregister the old standalone ipfs-sw.js that was replaced by the unified SW.
 * Browsers that had it will still have the registration lingering.
 */
async function unregisterStaleSW(): Promise<void> {
	if (!('serviceWorker' in navigator)) return
	const registrations = await navigator.serviceWorker.getRegistrations()
	for (const reg of registrations) {
		const swUrl = reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL
		if (swUrl?.includes('ipfs-sw')) {
			LOG('Unregistering stale ipfs-sw.js registration:', swUrl)
			await reg.unregister()
		}
	}
}

/**
 * Update SW config from current storage state
 */
export async function updateSWConfigFromState(): Promise<void> {
	const gateways = storageState.gateways
		?.filter(g => g.url)
		?.map(g => g.url!) ?? []

	if (gateways.length === 0) {
		// Use defaults
		gateways.push('https://trustless-gateway.link')
	}

	// Public IPFS gateways for IPNS resolution (defaults to env-derived list in storageState).
	// Copy out of the SolidJS store — its arrays are Proxy-wrapped and not reliably
	// structured-cloneable, so passing them straight into the SW's postMessage can throw
	// `DataCloneError: [object Array] could not be cloned`.
	const ipnsGatewayResolvers = [...(storageState.ipnsGatewayResolvers ?? ['https://ipfs.zt.ax'])]

	// w3name-style `/name/<ipns>` endpoints — graceful-transition fallback for names that
	// were published to w3name (name.web3.storage) but haven't propagated to the public IPFS
	// DHT, so the gateway resolvers above can't resolve them. The SW builds the request URL as
	// `${resolver}${name}`, so each base needs a trailing `/name/`. Configurable / disable-able
	// via VITE_IPNS_W3NAME_RESOLVERS (see storage.ts).
	const ipnsResolvers = (storageState.ipnsW3NameResolvers ?? ['https://name.web3.storage'])
		.map(base => `${base.replace(/\/+$/, '')}/name/`)

	await setConfig({
		gateways,
		ipnsResolvers,
		ipnsGatewayResolvers,
	})

	DEBUG('SW config updated:', { gateways, ipnsResolvers, ipnsGatewayResolvers })
}
